import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentTemplate, Divider, Grid, KeyValue, Typography } from 'web-library';



import { makeStyles } from 'tss-react/mui';

import DocumentBadge from '../../../../components/DocumentBadge';
import ContactDetails from '../particulars/ContactDetails';
import NextOfKinContactDetails from '../particulars/NextOfKinContactDetails';
import ParentParticulars from '../particulars/ParentParticulars';
import PersonalDetails from '../particulars/PersonalDetails';
import CAECertificateDetails from './CAECertificateDetails';
import CEQScore from './CEQScore';
import ExamDetails from './ExamDetails';
import ExamFee from './ExamFee';
import PaymentDetails from './PaymentDetails';
import PersonDeclaring from './PersonDeclaring';
import SchoolDetails from './SchoolDetails';
import SiblingDetails from './SiblingDetails';
import CandidateDetails from './spers/CandidateDetails';
import { stringToLongDateAndShortTime } from '../../../../utils/DateUtils';
import { useUpdateApplication } from '../../logic/useUpdateApplication';
import ViewAuditLog from './ViewAuditLog';
import OpsAdminForm from './OpsAdminForm';
import OpsAdminButton from './OpsAdminButton';

interface IDropdown {
  code: string;
  description: string;
}

interface IApplicationStatusData {
  status: string;
  candidateRemark: string;
  internalRemark: string;
  adminUpdatedByName: string;
  adminUpdatedAt: string;
  lockedByUser?: string;
  editable: boolean;
}

interface IOpsAdminData {
  putNonNationalExamApplicationStatus: any;
  applicationStatusDropdown: IDropdown[];
  applicationStatusData: IApplicationStatusData;
}

interface IConfirmAndPayList {
  data: any;
  isAEIS: boolean;
  opsAdminData?: IOpsAdminData;
  isLoadingLastUpdated: boolean;
  hasEditAccess: boolean;
}

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  tableCaption: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(24),
  },
  examFeeDetails: {
    paddingBottom: theme.spacing(6),
  },
  selectedSubjects: {
    paddingTop: theme.spacing(6),
  },
  amendmentError: {
    marginBottom: theme.spacing(3),
  },
  subheader: {
    marginBottom: theme.spacing(6),
  },
  documentBadgeContainer: {
    marginTop: theme.spacing(4),
  },
  inputSection: {
    marginBottom: theme.spacing(4),
  },
  viewLog: {
    marginTop: theme.spacing(6),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1),
      paddingLeft: 0,
    },
  },
  bottomButtonGroup: {
    marginBottom: theme.spacing(7),
  },
  twinButtonGroup: {
    marginBottom: theme.spacing(4),
  },
}));

const OpsAdminReviewApplication = ({
  data,
  isAEIS,
  opsAdminData,
  isLoadingLastUpdated,
  hasEditAccess,
}: IConfirmAndPayList) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const appStatusDefaultValue = opsAdminData?.applicationStatusDropdown.find(
    (d) => d.code === opsAdminData.applicationStatusData.status
  );
  const { formHook } = useUpdateApplication({
    formDefaultValues: {
      applicationStatus: appStatusDefaultValue || undefined,
      internalRemarks: opsAdminData?.applicationStatusData.internalRemark || '',
      candidateRemarks: opsAdminData?.applicationStatusData.candidateRemark || '',
    },
  });
  const { formState } = formHook;
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(true);

  useEffect(() => {
    setDisableButton(!formState.isDirty);
  }, [formState.isDirty]);

  const applicationDateTime = data.applicationStatus?.applicationDateTime
    ? stringToLongDateAndShortTime(data.applicationStatus.applicationDateTime)
    : 'N/A';

  const handleOpenAuditLog = () => {
    setIsOpenDialog(true);
  };

  const handleCloseAuditLog = () => {
    setIsOpenDialog(false);
  };

  return (
    <>
      <ContentTemplate isContinuable type="full">
        <Grid container direction="row">
          <Grid md={6} sm={12}>
            <KeyValue
              keyText={t('exam->>RegistrationSummary.ApplicationId')}
              valueText={data.applicationStatus?.applicationID}
            />
          </Grid>
        </Grid>
        <Grid container direction="row">
          <Grid md={6} sm={12}>
            <KeyValue
              keyText={t('exam->>RegistrationSummary.ApplicationDateTime')}
              valueText={applicationDateTime}
            />
          </Grid>
        </Grid>

        <PersonalDetails
          examCandidateDetail={data.candidateInfo}
          sameRowName
          isAEIS={isAEIS}
          isSPERS={!isAEIS}
          defaultExpanded
        />
        <ContactDetails
          contactDetails={data.candidateInfo}
          sameRowPhone
          sameRowAddress
          isAEIS={isAEIS}
          isSPERS={!isAEIS}
          defaultExpanded
        />
        <NextOfKinContactDetails {...data.candidateInfo} defaultExpanded />
        {isAEIS && (
          <ParentParticulars
            parentDetail={{
              fatherDetail: data.fatherDetail,
              motherDetail: data.motherDetail,
            }}
          />
        )}
        <PersonDeclaring
          personDetails={{
            personDeclaring: data.personDeclaring,
            personDeclaringId: data.personDeclaringId,
          }}
        />
        {!isAEIS && (
          <>
            <Divider paddingTop />
            <CandidateDetails
              candidateDetails={{
                certificateNo: data.candidateInfo.birthCertNo,
                dateAttained: data.candidateInfo.sprAttainedDate,
                motherTongueLanguage: data.mtLanguage,
              }}
            />
          </>
        )}
        <Divider paddingTop />
        <ExamDetails
          examDetails={{
            examYear: data.examYear,
            admissionLevel: data.admissionLevel,
            examLevel: data.examLevel,
            mtLanguage: data.mtLanguage,
            isAEIS,
          }}
        />
        <Divider paddingTop />
        {isAEIS && (
          <>
            {data.caeCertificationDetail && (
              <CAECertificateDetails certificateDetails={data.caeCertificationDetail} />
            )}
            {data.ceqScoreDetail && <CEQScore score={data.ceqScoreDetail} />}
          </>
        )}
        <SchoolDetails schoolDetails={data.schoolDetails} isAEIS={isAEIS} />
        {isAEIS && (
          <SiblingDetails siblingDetails={data.siblingDetails} examType={data.examLevel} />
        )}
        {data.supportingDocument.length ? (
          <>
            <Divider paddingTop />
            <Typography variant="h3" className={classes.subheader}>
              {t('exam->>NonNationalExam.SupportingDocuments')}
            </Typography>
            {data.supportingDocument.map((item: string) => (
              <DocumentBadge
                key={item}
                className={classes.documentBadgeContainer}
                documentName={item}
              />
            ))}
          </>
        ) : null}
        <Divider paddingTop />
        <ExamFee
          feeDetails={{
            regNumber: data.gstRegistrationNumber,
            totalExamFee: data.payment.totalExamFee,
            totalGst: data.payment.totalGst,
            totalFee: data.payment.totalFee,
          }}
        />
        <Divider paddingTop />
        <PaymentDetails paymentDetails={data.payment} />
        {opsAdminData && (
          <>
            <Divider paddingTop />
            <OpsAdminForm
              formData={opsAdminData}
              classes={classes}
              handleOpenAuditLog={handleOpenAuditLog}
              t={t}
              formHook={formHook}
              isLoadingLastUpdated={isLoadingLastUpdated}
              hasEditAccess={hasEditAccess}
            />
          </>
        )}
      </ContentTemplate>
      {opsAdminData && (
        <OpsAdminButton
          formData={opsAdminData}
          classes={classes}
          t={t}
          formHook={formHook}
          disableButton={disableButton}
          hasEditAccess={hasEditAccess}
        />
      )}
      {opsAdminData && <ViewAuditLog onClose={handleCloseAuditLog} isOpenDialog={isOpenDialog} />}
    </>
  );
};

export default OpsAdminReviewApplication;
